import type { Point } from '@trippath/shared';
import type { TFunction } from 'i18next';

import { wall } from '../../lib/clock';
import type * as Eclipses from '../../lib/eclipses';

/** The eclipses' own library, fetched once a calendar is open. */
export type EclipseSky = typeof Eclipses;

/** A day's eclipse, said: which it is, and how it is seen from the stop. */
export interface EclipseSaid {
  body: 'sun' | 'moon';
  name: string;
  seen: string;
}

/** Where a day is spent: its town, where it is, by what clock. */
export interface Where {
  city: string;
  place: Point | null;
  zone: string;
}

/**
 * The eclipses deepest on a day by its stop's clock: `Täielik päikesevarjutus` and, from the
 * stop, `Luxor: täielik, 10:38–13:20, suurim 11:58` — `Tartu: osaline, 11:09–13:04, suurim
 * 12:13 · 11% päikesest kaetud`, `Tokyo: siit ei paista`; a part of it seen while the sun or the
 * moon rises or sets, so said.
 */
export function eclipsesSaid(
  sky: EclipseSky,
  day: string,
  where: Where,
  t: TFunction,
  language: string,
): EclipseSaid[] {
  const clock = (at: number) => new Date(wall(where.zone, at)).toISOString().slice(11, 16);
  const share = new Intl.NumberFormat(language, { style: 'percent' });
  return sky.eclipsesOn(day, where.zone).map((eclipse) => {
    const name = t(`calendar.eclipse.${eclipse.body}.${eclipse.look}`);
    if (where.place === null) return { body: eclipse.body, name, seen: '' };
    const seen = sky.seenFrom(eclipse, where.place);
    if (seen.sight === 'none') {
      return {
        body: eclipse.body,
        name,
        seen: t('calendar.eclipseNone', { city: where.city }),
      };
    }
    // All of the sun covered, or all of the moon in the shadow, goes without saying; the half
    // shadow covers none of it.
    const covered =
      seen.look === 'total' || seen.look === 'penumbral'
        ? []
        : [
            t(
              eclipse.body === 'sun'
                ? 'calendar.eclipseSunCovered'
                : 'calendar.eclipseMoonCovered',
              {
                // Short of all of it, never rounded up to all: `99%`.
                share: share.format(Math.floor(seen.covered * 100) / 100),
              },
            ),
          ];
    const part =
      seen.sight === 'part'
        ? [t(eclipse.body === 'sun' ? 'calendar.eclipsePartSun' : 'calendar.eclipsePartMoon')]
        : [];
    return {
      body: eclipse.body,
      name,
      seen: [
        t('calendar.eclipseSeen', {
          city: where.city,
          look: t(`calendar.eclipseLook.${seen.look}`),
          from: clock(seen.begins),
          until: clock(seen.ends),
          peak: clock(seen.peak),
        }),
        ...covered,
        ...part,
      ].join(' · '),
    };
  });
}
